import React, { useEffect, useState, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CalendarIcon, DollarSignIcon, UsersIcon, CreditCardIcon, UserIcon, MailIcon, PhoneIcon, CheckIcon, ArrowLeftIcon, ShieldIcon } from 'lucide-react';
export function BookingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [participants, setParticipants] = useState(1);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    specialRequests: '',
    cardName: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  // Get all programs (simplified version - in a real app this would come from API or context)
  const programs = [{
    id: 'boxing-bootcamp',
    title: 'Boxing & Fitness Bootcamp with Chris Clements',
    category: 'Fitness',
    description: 'Train like a champion with professional boxer Chris Clements in an intensive boxing and fitness bootcamp designed to push your limits and transform your mindset.',
    duration: '3 days, 2 nights',
    price: '$1,599 per person',
    priceValue: 1599,
    groupSize: '15-25 participants',
    dates: ['Jan 26-28, 2025'],
    location: 'Hard Rock Hotel Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    id: 'singles-social',
    title: 'Singles Social',
    category: 'Social',
    description: 'Connect with like-minded singles in a relaxed beach setting with organized social activities and adventures.',
    duration: '3 days, 2 nights',
    price: '$1,299 per person',
    priceValue: 1299,
    groupSize: '20-30 participants',
    dates: ['May 15-17, 2024', 'July 10-12, 2024', 'Sept 5-7, 2024'],
    location: 'Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    id: 'couples-reset',
    title: 'Couples Reset & Vow-Renewal',
    category: 'Relationship',
    description: 'Reconnect and strengthen your relationship with a meaningful retreat designed for couples seeking renewal.',
    duration: '4 days, 3 nights',
    price: '$2,499 per couple',
    priceValue: 2499,
    groupSize: '10-15 couples',
    dates: ['Feb 14-17, 2024', 'June 20-23, 2024', 'Oct 12-15, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://images.unsplash.com/photo-1546032996-6dfacbacbf3f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2067&q=80'
  }, {
    id: 'friends-reunion',
    title: 'Friends Reunion Weekend',
    category: 'Social',
    description: 'Reconnect with old friends in a stunning beach setting with activities designed to strengthen bonds and create new memories.',
    duration: '3 days, 2 nights',
    price: '$1,199 per person',
    priceValue: 1199,
    groupSize: 'Custom groups of 6-20 friends',
    dates: ['March 22-24, 2024', 'Aug 16-18, 2024', 'Nov 8-10, 2024'],
    location: 'Playa del Carmen, Mexico',
    image: 'https://images.unsplash.com/photo-1559494007-9f5847c49d94?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80'
  }, {
    id: 'family-beach',
    title: 'Family Beach & Kids Leadership Camp',
    category: 'Family',
    description: 'The perfect family getaway combining relaxation for parents and enriching activities for children.',
    duration: '5 days, 4 nights',
    price: '$3,999 for family of four',
    priceValue: 3999,
    groupSize: '10-15 families',
    dates: ['June 10-14, 2024', 'July 22-26, 2024', 'Aug 12-16, 2024'],
    location: 'Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    id: 'wellness-reset',
    title: 'Wellness Reset: Yoga & Breathwork',
    category: 'Wellness',
    description: 'Rejuvenate your mind, body, and spirit with a transformative wellness retreat focused on yoga and breathwork practices.',
    duration: '6 days, 5 nights',
    price: '$2,299 per person',
    priceValue: 2299,
    groupSize: '15-20 participants',
    dates: ['Jan 15-20, 2024', 'April 10-15, 2024', 'Oct 5-10, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o1fjDw612HCk6Xa8RMhNyh/Yoga.webp',
    highlights: ['Daily yoga sessions with expert instructors', 'Breathwork and meditation workshops', 'Nutritional counseling', 'Beachfront sunrise yoga', 'Eco-friendly resort accommodations']
  }, {
    id: 'golf-beach',
    title: 'Golf & Beach Games Weekend',
    category: 'Sports',
    description: 'The perfect blend of championship golf and beach relaxation for sports enthusiasts.',
    duration: '4 days, 3 nights',
    price: '$1,899 per person',
    priceValue: 1899,
    groupSize: '12-24 participants',
    dates: ['Feb 22-25, 2024', 'May 17-20, 2024', 'Nov 14-17, 2024'],
    location: 'Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    id: 'family-adventure',
    title: 'Family Adventure: Cenotes & Reefs',
    category: 'Family',
    description: 'An action-packed family adventure exploring the natural wonders of the Yucatan Peninsula.',
    duration: '5 days, 4 nights',
    price: '$4,499 for family of four',
    priceValue: 4499,
    groupSize: '8-10 families',
    dates: ['March 15-19, 2024', 'July 8-12, 2024', 'Dec 26-30, 2024'],
    location: 'Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1544551763-92ab472cad5d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    id: 'nutrition-wellness',
    title: 'Nutrition & Wellness Transformation',
    category: 'Wellness',
    description: 'Transform your relationship with food and discover sustainable nutrition strategies with expert guidance in a stunning beach setting.',
    duration: '5 days, 4 nights',
    price: '$2,499 per person',
    priceValue: 2499,
    groupSize: '15-20 participants',
    dates: ['March 8-12, 2024', 'Sept 20-24, 2024', 'Nov 15-19, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aEgUSDVN3SaRCTgZHE6TX2/Nutrition.webp',
    highlights: ['Personalized nutrition assessments', 'Cooking workshops with healthy recipes', 'Mindful eating practices', 'Farm-to-table dining experiences', 'Luxury boutique hotel accommodations', 'Meal planning and prep strategies']
  }, {
    id: 'women-leadership',
    title: 'Women in Leadership & Wellness',
    category: 'Professional',
    description: 'Empower yourself with a retreat designed specifically for women leaders combining professional development and wellness.',
    duration: '5 days, 4 nights',
    price: '$2,499 per person',
    priceValue: 2499,
    groupSize: '15-20 participants',
    dates: ['March 8-12, 2024', 'Sept 20-24, 2024', 'Nov 15-19, 2024'],
    location: 'Tulum, Mexico',
    image: 'https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80'
  }, {
    id: 'community-offsite',
    title: 'Desta Community Offsite Weekend',
    category: 'Community',
    description: 'Join the Desta community for a weekend of connection, learning, and relaxation with like-minded individuals.',
    duration: '3 days, 2 nights',
    price: '$999 per person',
    priceValue: 999,
    groupSize: '30-50 participants',
    dates: ['April 26-28, 2024', 'Aug 23-25, 2024', 'Oct 18-20, 2024'],
    location: 'Playa del Carmen, Mexico',
    image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
  }, {
    id: 'yacht-social',
    title: 'Yacht Social & Sunset Jams',
    category: 'Luxury',
    description: 'Experience the ultimate luxury weekend with yacht parties, live music, and exclusive beach events.',
    duration: '3 days, 2 nights',
    price: '$2,999 per person',
    priceValue: 2999,
    groupSize: '20-30 participants',
    dates: ['May 24-26, 2024', 'July 19-21, 2024', 'Sept 13-15, 2024'],
    location: 'Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1605281317010-fe5ffe798166?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2072&q=80'
  }, {
    id: 'kids-sports',
    title: 'Kids Sports & Leadership Camp',
    category: 'Youth',
    description: 'A specialized program for young athletes combining sports training with leadership development.',
    duration: '7 days, 6 nights',
    price: '$1,899 per child',
    priceValue: 1899,
    groupSize: '20-30 participants (ages 10-16)',
    dates: ['June 16-22, 2024', 'July 14-20, 2024', 'Aug 4-10, 2024'],
    location: 'Cancun, Mexico',
    image: 'https://images.unsplash.com/photo-1526307616774-60d0098f7642?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80'
  }, {
    id: 'startup-bootcamp',
    title: 'Build on the Beach - The Entrepreneurs Bootcamp',
    category: 'Professional',
    description: 'An intensive program for entrepreneurs combining business development workshops with networking in a relaxed setting.',
    duration: '5 days, 4 nights',
    price: '$2,299 per person',
    priceValue: 2299,
    groupSize: '15-25 participants',
    dates: ['Feb 5-9, 2024', 'May 6-10, 2024', 'Oct 7-11, 2024'],
    location: 'Playa del Carmen, Mexico',
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    id: 'founders-investors',
    title: 'Founders & Investors Circle (invite-only)',
    category: 'Exclusive',
    description: 'An exclusive gathering for successful founders and investors to connect, share insights, and explore opportunities.',
    duration: '4 days, 3 nights',
    price: '$4,999 per person',
    priceValue: 4999,
    groupSize: '15-20 participants (invitation only)',
    dates: ['March 1-4, 2024', 'Sept 26-29, 2024', 'Dec 5-8, 2024'],
    location: 'Private Villa, Riviera Maya, Mexico',
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  // Check if a program was passed in location state
  useEffect(() => {
    if (location.state && location.state.program) {
      setSelectedProgram(location.state.program);
      if (location.state.program.dates && location.state.program.dates.length > 0) {
        setSelectedDate(location.state.program.dates[0]);
      }
    }
  }, [location]);
  const handleProgramChange = e => {
    const program = programs.find(p => p.id === e.target.value);
    setSelectedProgram(program);
    if (program && program.dates && program.dates.length > 0) {
      setSelectedDate(program.dates[0]);
    } else {
      setSelectedDate('');
    }
  };
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const calculateTotal = () => {
    if (!selectedProgram) return 0;
    // Handle family packages
    if (selectedProgram.id === 'family-beach' || selectedProgram.id === 'family-adventure') {
      return selectedProgram.priceValue;
    }
    // Handle couples package
    if (selectedProgram.id === 'couples-reset') {
      return selectedProgram.priceValue * Math.ceil(participants / 2);
    }
    // Standard per person pricing
    return selectedProgram.priceValue * participants;
  };
  const handleSubmit = e => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsComplete(true);
    }, 1500);
  };
  const nextStep = () => {
    window.scrollTo(0, 0);
    setStep(step + 1);
  };
  const prevStep = () => {
    window.scrollTo(0, 0);
    setStep(step - 1);
  };
  const isStep1Valid = selectedProgram && selectedDate && participants > 0;
  const isStep2Valid = formData.firstName && formData.lastName && formData.email && formData.phone;
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-839">
      {/* Hero */}
      <section className="bg-[#0099A1] py-12" data-id="element-840">
        <div className="container mx-auto px-4 md:px-6" data-id="element-841">
          <div className="flex items-center mb-2" data-id="element-842">
            <button onClick={() => navigate(-1)} className="text-white flex items-center hover:underline" data-id="element-843">
              <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-844" />
              Back
            </button>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white" data-id="element-845">
            {isComplete ? 'Booking Confirmed!' : 'Book Your Comeback Program'}
          </h1>
        </div>
      </section>
      {/* Progress Steps */}
      {!isComplete && <section className="bg-white border-b" data-id="element-846">
          <div className="container mx-auto px-4 md:px-6 py-4" data-id="element-847">
            <div className="flex justify-between max-w-3xl mx-auto" data-id="element-848">
              <div className={`flex flex-col items-center ${step >= 1 ? 'text-[#0099A1]' : 'text-gray-400'}`} data-id="element-849">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#0099A1] text-white' : 'bg-gray-200'}`} data-id="element-850">
                  1
                </div>
                <span className="text-sm mt-1" data-id="element-851">Select Program</span>
              </div>
              <div className={`flex flex-col items-center ${step >= 2 ? 'text-[#0099A1]' : 'text-gray-400'}`} data-id="element-852">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#0099A1] text-white' : 'bg-gray-200'}`} data-id="element-853">
                  2
                </div>
                <span className="text-sm mt-1" data-id="element-854">Your Details</span>
              </div>
              <div className={`flex flex-col items-center ${step >= 3 ? 'text-[#0099A1]' : 'text-gray-400'}`} data-id="element-855">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-[#0099A1] text-white' : 'bg-gray-200'}`} data-id="element-856">
                  3
                </div>
                <span className="text-sm mt-1" data-id="element-857">Payment</span>
              </div>
            </div>
          </div>
        </section>}
      {/* Main Content */}
      <section className="py-12" data-id="element-858">
        <div className="container mx-auto px-4 md:px-6" data-id="element-859">
          {isComplete ? <Card className="max-w-3xl mx-auto" data-id="element-860">
              <CardBody className="p-8 text-center" data-id="element-861">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-862">
                  <CheckIcon className="h-8 w-8 text-green-600" data-id="element-863" />
                </div>
                <h2 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-864">
                  Thank You for Your Booking!
                </h2>
                <p className="text-[#374047]/80 mb-6" data-id="element-865">
                  Your reservation for {selectedProgram?.title} has been
                  confirmed. We've sent a confirmation email to {formData.email}{' '}
                  with all the details.
                </p>
                <div className="bg-[#F4E8D0]/30 p-6 rounded-lg mb-6 text-left" data-id="element-866">
                  <h3 className="text-lg font-semibold text-[#374047] mb-4" data-id="element-867">
                    Booking Summary
                  </h3>
                  <div className="space-y-2" data-id="element-868">
                    <div className="flex justify-between" data-id="element-869">
                      <span className="text-[#374047]/70" data-id="element-870">Program:</span>
                      <span className="font-medium" data-id="element-871">
                        {selectedProgram?.title}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-872">
                      <span className="text-[#374047]/70" data-id="element-873">Date:</span>
                      <span className="font-medium" data-id="element-874">{selectedDate}</span>
                    </div>
                    <div className="flex justify-between" data-id="element-875">
                      <span className="text-[#374047]/70" data-id="element-876">Participants:</span>
                      <span className="font-medium" data-id="element-877">{participants}</span>
                    </div>
                    <div className="flex justify-between" data-id="element-878">
                      <span className="text-[#374047]/70" data-id="element-879">Location:</span>
                      <span className="font-medium" data-id="element-880">
                        {selectedProgram?.location}
                      </span>
                    </div>
                    <div className="border-t pt-2 mt-2" data-id="element-881">
                      <div className="flex justify-between font-semibold" data-id="element-882">
                        <span data-id="element-883">Total:</span>
                        <span data-id="element-884">${calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-[#374047]/80 mb-8" data-id="element-885">
                  Our team will contact you shortly with more information about
                  your upcoming experience. If you have any questions, please
                  don't hesitate to reach out to us.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-886">
                  <Button variant="primary" to="/" data-id="element-887">
                    Return to Home
                  </Button>
                  <Button variant="outline" to="/comeback-programs" data-id="element-888">
                    Browse More Programs
                  </Button>
                </div>
              </CardBody>
            </Card> : <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-889">
              {/* Form Section */}
              <div className="md:col-span-2" data-id="element-890">
                <Card data-id="element-891">
                  <CardBody className="p-6" data-id="element-892">
                    {step === 1 && <form data-id="element-893">
                        <h2 className="text-xl font-bold text-[#374047] mb-6" data-id="element-894">
                          Select Your Program
                        </h2>
                        <div className="mb-6" data-id="element-895">
                          <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-896">
                            Program *
                          </label>
                          <select className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" value={selectedProgram?.id || ''} onChange={handleProgramChange} required data-id="element-897">
                            <option value="" data-id="element-898">Select a Program</option>
                            {programs.map(program => <option key={program.id} value={program.id} data-id="element-899">
                                {program.title} - {program.price}
                              </option>)}
                          </select>
                        </div>
                        {selectedProgram && <>
                            <div className="mb-6" data-id="element-900">
                              <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-901">
                                Date *
                              </label>
                              <select className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} required data-id="element-902">
                                <option value="" data-id="element-903">Select a Date</option>
                                {selectedProgram.dates.map((date, index) => <option key={index} value={date} data-id="element-904">
                                    {date}
                                  </option>)}
                              </select>
                            </div>
                            <div className="mb-6" data-id="element-905">
                              <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-906">
                                Number of Participants *
                              </label>
                              <div className="flex items-center" data-id="element-907">
                                <button type="button" className="p-2 border border-gray-300 rounded-l-md" onClick={() => setParticipants(Math.max(1, participants - 1))} data-id="element-908">
                                  -
                                </button>
                                <input type="number" min="1" value={participants} onChange={e => setParticipants(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 p-2 text-center border-y border-gray-300" data-id="element-909" />
                                <button type="button" className="p-2 border border-gray-300 rounded-r-md" onClick={() => setParticipants(participants + 1)} data-id="element-910">
                                  +
                                </button>
                              </div>
                              <p className="text-sm text-[#374047]/70 mt-1" data-id="element-911">
                                {selectedProgram.id === 'family-beach' || selectedProgram.id === 'family-adventure' ? 'Price is per family (up to 4 people)' : selectedProgram.id === 'couples-reset' ? 'Price is per couple' : 'Price is per person'}
                              </p>
                            </div>
                          </>}
                        <div className="flex justify-end" data-id="element-912">
                          <Button variant="primary" type="button" onClick={nextStep} disabled={!isStep1Valid} data-id="element-913">
                            Continue to Personal Details
                          </Button>
                        </div>
                      </form>}
                    {step === 2 && <form data-id="element-914">
                        <h2 className="text-xl font-bold text-[#374047] mb-6" data-id="element-915">
                          Your Details
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-916">
                          <div data-id="element-917">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-918">
                              First Name *
                            </label>
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-919" />
                          </div>
                          <div data-id="element-920">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-921">
                              Last Name *
                            </label>
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-922" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-923">
                          <div data-id="element-924">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-925">
                              Email Address *
                            </label>
                            <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-926" />
                          </div>
                          <div data-id="element-927">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-928">
                              Phone Number *
                            </label>
                            <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-929" />
                          </div>
                        </div>
                        <div className="mb-6" data-id="element-930">
                          <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-931">
                            Address
                          </label>
                          <input type="text" name="address" value={formData.address} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-932" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-933">
                          <div data-id="element-934">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-935">
                              City
                            </label>
                            <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-936" />
                          </div>
                          <div data-id="element-937">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-938">
                              Country
                            </label>
                            <input type="text" name="country" value={formData.country} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-939" />
                          </div>
                        </div>
                        <div className="mb-6" data-id="element-940">
                          <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-941">
                            Special Requests or Notes
                          </label>
                          <textarea name="specialRequests" value={formData.specialRequests} onChange={handleInputChange} rows={3} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" data-id="element-942"></textarea>
                        </div>
                        <div className="flex justify-between" data-id="element-943">
                          <Button variant="outline" type="button" onClick={prevStep} data-id="element-944">
                            Back
                          </Button>
                          <Button variant="primary" type="button" onClick={nextStep} disabled={!isStep2Valid} data-id="element-945">
                            Continue to Payment
                          </Button>
                        </div>
                      </form>}
                    {step === 3 && <form onSubmit={handleSubmit} data-id="element-946">
                        <h2 className="text-xl font-bold text-[#374047] mb-6" data-id="element-947">
                          Payment Information
                        </h2>
                        <div className="mb-6" data-id="element-948">
                          <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-949">
                            Name on Card *
                          </label>
                          <input type="text" name="cardName" value={formData.cardName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-950" />
                        </div>
                        <div className="mb-6" data-id="element-951">
                          <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-952">
                            Card Number *
                          </label>
                          <input type="text" name="cardNumber" value={formData.cardNumber} onChange={handleInputChange} placeholder="1234 5678 9012 3456" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-953" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-954">
                          <div data-id="element-955">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-956">
                              Expiration Date *
                            </label>
                            <input type="text" name="cardExpiry" value={formData.cardExpiry} onChange={handleInputChange} placeholder="MM/YY" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-957" />
                          </div>
                          <div data-id="element-958">
                            <label className="block text-sm font-medium text-[#374047] mb-2" data-id="element-959">
                              CVC *
                            </label>
                            <input type="text" name="cardCvc" value={formData.cardCvc} onChange={handleInputChange} placeholder="123" className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#0099A1] focus:border-[#0099A1]" required data-id="element-960" />
                          </div>
                        </div>
                        <div className="mb-6 bg-gray-50 p-4 rounded-md flex items-start" data-id="element-961">
                          <ShieldIcon className="h-5 w-5 text-[#0099A1] mt-0.5 mr-3 flex-shrink-0" data-id="element-962" />
                          <p className="text-sm text-[#374047]/70" data-id="element-963">
                            Your payment information is secured with
                            industry-standard encryption. We do not store your
                            full card details on our servers.
                          </p>
                        </div>
                        <div className="flex justify-between" data-id="element-964">
                          <Button variant="outline" type="button" onClick={prevStep} data-id="element-965">
                            Back
                          </Button>
                          <Button variant="primary" type="submit" disabled={isSubmitting} data-id="element-966">
                            {isSubmitting ? 'Processing...' : 'Complete Booking'}
                          </Button>
                        </div>
                      </form>}
                  </CardBody>
                </Card>
              </div>
              {/* Summary Section */}
              <div data-id="element-967">
                <div className="sticky top-32" data-id="element-968">
                  <Card data-id="element-969">
                    <CardBody className="p-6" data-id="element-970">
                      <h3 className="text-lg font-bold text-[#374047] mb-4" data-id="element-971">
                        Booking Summary
                      </h3>
                      {selectedProgram ? <>
                          <div className="mb-4" data-id="element-972">
                            <img src={selectedProgram.image} alt={selectedProgram.title} className="w-full h-40 object-cover rounded-md" data-id="element-973" />
                          </div>
                          <h4 className="font-semibold text-[#374047] mb-4" data-id="element-974">
                            {selectedProgram.title}
                          </h4>
                          <div className="space-y-3 mb-6" data-id="element-975">
                            <div className="flex items-start" data-id="element-976">
                              <CalendarIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-977" />
                              <div data-id="element-978">
                                <p className="text-sm font-medium text-[#374047]" data-id="element-979">
                                  Date
                                </p>
                                <p className="text-sm text-[#374047]/70" data-id="element-980">
                                  {selectedDate || 'Not selected'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start" data-id="element-981">
                              <UsersIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-982" />
                              <div data-id="element-983">
                                <p className="text-sm font-medium text-[#374047]" data-id="element-984">
                                  Participants
                                </p>
                                <p className="text-sm text-[#374047]/70" data-id="element-985">
                                  {participants}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start" data-id="element-986">
                              <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-987" />
                              <div data-id="element-988">
                                <p className="text-sm font-medium text-[#374047]" data-id="element-989">
                                  Price
                                </p>
                                <p className="text-sm text-[#374047]/70" data-id="element-990">
                                  {selectedProgram.price}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="border-t pt-4" data-id="element-991">
                            <div className="flex justify-between mb-2" data-id="element-992">
                              <span className="font-medium" data-id="element-993">Subtotal</span>
                              <span data-id="element-994">${calculateTotal().toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between mb-2" data-id="element-995">
                              <span className="font-medium" data-id="element-996">Taxes & Fees</span>
                              <span data-id="element-997">Included</span>
                            </div>
                            <div className="flex justify-between font-bold text-lg mt-4" data-id="element-998">
                              <span data-id="element-999">Total</span>
                              <span data-id="element-1000">${calculateTotal().toLocaleString()}</span>
                            </div>
                          </div>
                        </> : <div className="text-center py-8 text-[#374047]/70" data-id="element-1001">
                          <p data-id="element-1002">Please select a program to see the summary</p>
                        </div>}
                    </CardBody>
                  </Card>
                  <div className="mt-4 bg-white p-4 rounded-md border" data-id="element-1003">
                    <h4 className="font-semibold text-[#374047] mb-2" data-id="element-1004">
                      Need Assistance?
                    </h4>
                    <p className="text-sm text-[#374047]/70 mb-3" data-id="element-1005">
                      Our team is here to help with your booking.
                    </p>
                    <div className="flex items-center text-sm text-[#0099A1]" data-id="element-1006">
                      <PhoneIcon className="h-4 w-4 mr-2" data-id="element-1007" />
                      <span data-id="element-1008">+1 415 417 0322</span>
                    </div>
                    <div className="flex items-center text-sm text-[#0099A1] mt-1" data-id="element-1009">
                      <MailIcon className="h-4 w-4 mr-2" data-id="element-1010" />
                      <span data-id="element-1011">bookings@desta.meet</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </section>
    </div>;
}